import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/layout/Footer';
import WiwInvestigationHeader from '../components/layout/WiwInvestigationHeader';

const MODULES = [
  {
    id: 'corporate',
    title: 'Corporate Due Diligence',
    body:
      'Local AML / sanctions screening, email security posture (DMARC), domain infrastructure enrichment, and court / litigation registry signals for professional counterparty checks.',
  },
  {
    id: 'person',
    title: 'Person Search',
    body:
      'Digital footprint mapping, associate and professional profile context, and bilingual RO/EN evidence handling for investigation-grade person resolution.',
  },
  {
    id: 'email',
    title: 'Email Intelligence',
    body:
      'Known breach visibility plus Dark Web exposure advisory limited to public index metadata — credentials and dump bodies are never ingested.',
  },
  {
    id: 'phone',
    title: 'Phone Lookup',
    body:
      'Spam / scam network signals, line type cues (including VOIP risk), and business presence discovery with noise filtered for analysts.',
  },
];

/**
 * B2B acquisition landing — clear module value props for Google Ads.
 * Scrolls inside ``#root`` (global overflow is locked for the Home workspace).
 */
export default function LandingPage() {
  return (
    <div
      className="relative flex h-[100dvh] max-h-[100dvh] min-h-0 w-full flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain bg-[#0f172a] text-slate-100"
      data-testid="landing-page"
    >
      <div className="sticky top-0 z-40 flex shrink-0 items-stretch border-b border-white/10 bg-[#0f172a]/95 backdrop-blur-xl">
        <div className="min-w-0 flex-1 [&_.wiw-inv-header]:static [&_.wiw-inv-header]:border-0 [&_.wiw-inv-header]:bg-transparent [&_.wiw-inv-header]:backdrop-blur-none">
          <WiwInvestigationHeader />
        </div>
        <div className="flex shrink-0 items-center pr-3 pt-[max(0.25rem,env(safe-area-inset-top))] sm:pr-6">
          <Link
            to="/"
            className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-100 transition hover:border-cyan-400/40 hover:bg-cyan-500/10 hover:text-cyan-100 sm:px-4 sm:text-sm"
            data-testid="landing-page-close"
          >
            Close
          </Link>
        </div>
      </div>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 sm:px-8 sm:py-14">
        <header className="mx-auto max-w-3xl text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-cyan-400/80">
            cybertracer.ro
          </p>
          <h1 className="mt-3 font-['Orbitron',ui-sans-serif,sans-serif] text-3xl font-black uppercase tracking-[0.12em] text-cyan-400 sm:text-4xl">
            OSINT Due Diligence Workspace
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
            Know who you are dealing with — before you trust, hire, or partner. CyberTracer turns
            public OSINT into a live investigation board for compliance, security, and risk teams.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="rounded-lg border border-cyan-400/40 bg-cyan-500/20 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-cyan-50 hover:bg-cyan-500/30"
            >
              Open workspace
            </Link>
            <Link
              to="/privacy"
              className="rounded-lg border border-white/15 bg-slate-900/60 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-200 hover:border-white/30"
            >
              Privacy
            </Link>
          </div>
        </header>

        <section className="mt-12 grid gap-4 sm:grid-cols-2" aria-label="Core modules">
          {MODULES.map((mod) => (
            <article
              key={mod.id}
              className="rounded-2xl border border-white/10 bg-slate-900/50 p-5 shadow-lg shadow-black/20"
              data-testid={`landing-module-${mod.id}`}
            >
              <h2 className="text-sm font-black uppercase tracking-[0.14em] text-cyan-200">
                {mod.title}
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-slate-300 sm:text-sm">{mod.body}</p>
            </article>
          ))}
        </section>

        <p className="mx-auto mt-10 max-w-3xl text-center text-[11px] leading-relaxed text-slate-500">
          CyberTracer.ro surfaces publicly available OSINT metadata to support professional digital
          due diligence and risk assessment. Raw credentials or unauthorized databases are never
          ingested or stored.
        </p>
      </main>
      <Footer className="relative z-20 w-full shrink-0" />
    </div>
  );
}
